import pluralize from 'pluralize';
import { memo, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import { ChevronDown, Minus } from '@air/next-icons';
import { DropdownMenu } from '@air/primitive-dropdown-menu';
import { IconButton } from '@air/primitive-icon-button';
import { selectedItemsSelector } from '@air/redux-selected-items';
import { cn } from '@air/utils-tailwind';

import { SelectionList } from '~/components/v2/components/shared/selectionBar/components/SelectionList';
import { useSelectedItemsSummary } from '~/components/v2/components/shared/selectionBar/hooks/useSelectedItemsSummary';
import { useViewsSelectAllStore } from '~/components/v2/store/features/views/useViewsSelectAllStore';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { hasOnlyCanvasesSelectedSelector } from '~/store/selectedItems/selectors';

export type SelectionActionBarItemCountProps = {
  selectAllTotalCount?: number | null;
  omitBoardName?: boolean;
};

export const SelectionActionBarItemCount = memo(
  ({ selectAllTotalCount, omitBoardName }: SelectionActionBarItemCountProps) => {
    const selectedItems = useSelector(selectedItemsSelector);
    const { crossLocationSelectionEnabled } = useFeatureFlags();
    const selectAll = useViewsSelectAllStore((state) => state.selectAll);

    const { summary } = useSelectedItemsSummary({ selectAllTotalCount, omitBoardName });

    const hasOnlyCanvasesSelected = useSelector(hasOnlyCanvasesSelectedSelector);

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null);

    const handleCloseMenu = useCallback(() => {
      setIsMenuOpen(false);
    }, []);

    if (!crossLocationSelectionEnabled || selectAll || hasOnlyCanvasesSelected) {
      return (
        <div className="flex items-center gap-1 text-12 text-grey-1" data-testid="SELECTION_ACTION_BAR_ITEMS_SELECTED">
          {summary}
        </div>
      );
    }

    return (
      <DropdownMenu
        className="max-h-[400px] w-[400px] overflow-y-auto p-0"
        trigger={
          <div
            className={cn('flex items-center gap-1 text-12 text-grey-1', selectedItems.length > 1 && 'cursor-pointer')}
            data-testid="SELECTION_ACTION_BAR_ITEMS_SELECTED"
          >
            {summary}
            {selectedItems.length > 1 && (
              <ChevronDown className={cn('size-4 text-grey-1', isMenuOpen && 'rotate-180')} />
            )}
          </div>
        }
        size="large"
        open={isMenuOpen}
        onOpenChange={setIsMenuOpen}
        disabled={selectedItems.length <= 1}
        ref={(newRef) => setScrollElement(newRef)}
      >
        <div className="flex select-none flex-col">
          <div className="sticky top-0 z-30 flex w-full items-center justify-between gap-2.5 bg-grey-1 px-3 pt-3 pb-3">
            <div className="ml-1 font-medium text-14 text-grey-12">
              {selectAll && selectAllTotalCount !== null
                ? `All ${selectAllTotalCount} ${pluralize('item', selectAllTotalCount)} selected`
                : `${selectedItems.length} ${pluralize('item', selectedItems.length)} selected`}
            </div>
            <IconButton
              icon={Minus}
              onClick={handleCloseMenu}
              label="Close"
              appearance="ghost"
              color="grey"
              size="small"
            />
          </div>
          <SelectionList scrollElement={scrollElement} />
        </div>
      </DropdownMenu>
    );
  },
);

SelectionActionBarItemCount.displayName = 'SelectionActionBarItemCount';
